/** Telling somebody **why** they are looking at the sign-in form after their session ended.
 *
 * A session expires on the server's clock. When it ends while somebody is working, the next request
 * 401s, `main.tsx` says "nobody", and `RequireSession` swaps the screen for the sign-in form at the
 * same address. Before this the form simply appeared: no sentence said that anything had happened,
 * so the reader's first guess was a crash or a bad link.
 *
 * **What is stored is one flag and nothing else.** No token (the session is an httpOnly cookie this
 * code never sees), no address, no name, no figure, no record of where the reader was — the address
 * bar already says that, because the form is rendered in place. `sessionStorage` rather than
 * `localStorage`: it dies with the tab, so a notice cannot outlive the sitting it describes or turn
 * up for a shared machine's next visitor.
 *
 * **Two ways in, two ways out.** The flag is raised by a 401 from a data request
 * (`handleSessionLost`) and by the focus re-check of `/auth/me` finding the session gone
 * (`useMe`). It is cleared by the sign-in form having shown it once, and by an explicit sign-out —
 * somebody who chose to leave must not be told they were thrown out. A first visit never raises it.
 *
 * Storage can be refused (a private window, blocked site data), so every access is in `try/catch`
 * and an in-memory copy carries the flag when storage will not: the notice is a courtesy, and a
 * failure to remember it must never break sign-in.
 */

import type { QueryClient } from "@tanstack/react-query";

/** The cache key `useMe` uses. Repeated here rather than imported from `auth.ts`, which imports this
 * module: a cycle for one array. `sessionEnded.test.ts` asserts the two agree. */
export const ME_KEY = ["auth", "me"] as const;

/** Decided wording. The remedy is the sentence the reader needs: sign in, and you are back where you
 * were. It is true because the form is rendered in place and `SignInScreen` does not navigate away
 * when this notice was shown. */
export const SESSION_ENDED_NOTICE =
  "Your session ended, so you have been signed out. Sign in again to carry on where you were.";

/** The second sentence, said only when something typed was lost with the session (G-P9-08).
 *
 * "Sign in again to carry on where you were" is true of the **screen** - the form is rendered in place
 * and sign-in does not navigate away - and was read as true of a half-written form, which it is not:
 * a drawer's contents live in the page and the page has been replaced. They are **not kept anywhere**,
 * deliberately: `lib/drafts` (ADR-0112) stores identifiers and choices and never anything a person
 * typed in words, because a sentence left in a browser's storage on a shared machine is a larger loss
 * than the sentence. So the product says what happened instead of promising what it cannot do. */
export const SESSION_ENDED_TYPING_NOTICE =
  "What you had typed in the form that was open was not kept, so it will need typing again.";

const KEY = "emitiq.session-ended";
const TYPED_KEY = "emitiq.session-ended.typed";

/** The fallback for a browser that will not give us storage. Module state, so it is per page load. */
let inMemory = false;
let inMemoryTyped = false;

/** Whether an open dialog (a drawer's form) holds something somebody typed. Read from the page while
 * it is still there: the sign-in form replaces it within the same tick. A box with only whitespace in it
 * is nothing; a checkbox, a file chooser and a hidden field are not typing. */
export function typingInOpenDialog(): boolean {
  try {
    for (const dialog of document.querySelectorAll('[role="dialog"]')) {
      for (const field of dialog.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        "textarea, input",
      )) {
        const type = field instanceof HTMLInputElement ? field.type : "textarea";
        if (["hidden", "checkbox", "radio", "file", "button", "submit", "range"].includes(type)) continue;
        if (field.value.trim() !== "") return true;
      }
    }
  } catch {
    // No page to read: nothing was lost that can be named.
  }
  return false;
}

/** Raise the flag. Idempotent, and it **keeps** a "typing was lost" fact it has already recorded. */
export function markSessionEnded(): void {
  inMemory = true;
  const typed = typingInOpenDialog();
  if (typed) inMemoryTyped = true;
  try {
    window.sessionStorage.setItem(KEY, "1");
    if (typed) window.sessionStorage.setItem(TYPED_KEY, "1");
  } catch {
    // Storage refused: the in-memory copy above still carries it for this page load.
  }
}

/** Whether what was lost included something typed into an open form. */
export function sessionEndedLostTyping(): boolean {
  if (inMemoryTyped) return true;
  try {
    return window.sessionStorage.getItem(TYPED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Whether the sign-in form should say the session ended. Does not clear it — see
 * `clearSessionEnded`, which the form calls from an effect so that React's double-invoked
 * initialisers in development cannot consume the flag before it has been rendered. */
export function sessionEndedPending(): boolean {
  if (inMemory) return true;
  try {
    return window.sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/** Lower the flag. Called once the notice has been shown, and on an explicit sign-out. */
export function clearSessionEnded(): void {
  inMemory = false;
  inMemoryTyped = false;
  try {
    window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(TYPED_KEY);
  } catch {
    // Nothing to clear if storage is refused.
  }
}

/** What to do when the server says this session is over — registered once by `main.tsx`.
 *
 * **The session is set to null first, and the rest of the cache is dropped second.** The reverse
 * order looks equivalent and does nothing: `clear()` removes the `auth/me` query *and detaches its
 * observer*, so the `setQueryData` after it writes into a cache entry nothing is watching —
 * `useMe` keeps the signed-in user it already had, `RequireSession` keeps rendering the screen, and
 * the reader is left on a shell whose every figure has quietly become `0.0`. That is what the first
 * version of this did, and the browser is where it was caught.
 *
 * Setting the session first notifies that observer, which swaps in the sign-in form, which unmounts
 * every screen — so the queries being dropped afterwards have no observers left to restart them.
 *
 * **Idempotent by the cached fact rather than by a flag**, because clearing restarts whatever is
 * still in flight and those answers arrive back here. It is also what keeps this from raising the
 * notice after an explicit sign-out: `useLogout` has already said "nobody", so a straggling 401
 * returns here before it can mark anything.
 *
 * It lives here rather than in `main.tsx` so a test can drive it against a real `QueryClient`;
 * `main.tsx` renders the whole app on import.
 */
export function handleSessionLost(queryClient: QueryClient): void {
  const known = queryClient.getQueryData(ME_KEY);
  if (known === null) return;
  // **Only a session that was actually held can have ended.** On a first visit the screen's data
  // requests race `/auth/me`: they 401 while the cache still says `undefined` ("not asked yet"),
  // and marking then told every anonymous visitor their session had ended — found in a real browser
  // on 2026-10-30, after the unit tests (which always had a session cached first) passed. Unknown
  // still resolves to "nobody", as it always did; it just does not claim a history it lacks.
  if (known) markSessionEnded();
  queryClient.setQueryData(ME_KEY, null);
  // Everything except the session itself. A reader at a sign-in form with last session's figures
  // behind it is looking at data they are no longer entitled to see.
  queryClient.removeQueries({
    predicate: (query) => JSON.stringify(query.queryKey) !== JSON.stringify(ME_KEY),
  });
}
